<template>
    <div class="vacancy-card card">
        <form @submit.prevent="handleSubmit" class="form-grid">
            <!-- Title full width -->
            <div class="form-row full">
                <label class="field-label" for="title">Title</label>
                <input
                    class="field-input"
                    v-model="vacancyFormData.title"
                    id="title"
                    type="text"
                    required
                    :disabled="!isAdmin"
                />
            </div>

            <!-- Description full width -->
            <div class="form-row full">
                <label class="field-label" for="description">Description</label>
                <textarea
                    class="field-input"
                    v-model="vacancyFormData.description"
                    id="description"
                    rows="5"
                    required
                    :disabled="!isAdmin"
                ></textarea>
            </div>

            <div class="form-row">
                <label class="field-label" for="requiredLevel">Required Level</label>
                <div class="skills-controls">
                    <el-select
                        class="wide"
                        v-model="vacancyFormData.requiredLevel"
                        placeholder="Choose from the list"
                        id="requiredLevel"
                        :disabled="!isAdmin"
                    >
                        <el-option v-for="level in descriptions.activityCVLevel || []" :key="level.value"
                            :label="level.description" :value="level.value" />
                    </el-select>
                </div>
            </div>

            <div class="form-row">
                <label class="field-label" for="status">Status</label>
                <div class="skills-controls">
                    <el-select
                        class="wide"
                        v-model="vacancyFormData.status"
                        placeholder="Choose from the list"
                        id="status"
                        :disabled="!isAdmin"
                    >
                        <el-option
                            v-for="status in filteredStatuses"
                            :key="status.value"
                            :label="status.description"
                            :value="status.value"
                        />
                    </el-select>
                </div>
            </div>

            <div class="form-actions">
                <el-button type="primary" @click="handleClose">{{isAdmin ? 'Cancel' : 'Close'}}</el-button>
                <!-- Save disabled when not admin -->
                <el-button v-if="isAdmin" type="primary" native-type="submit">Save</el-button>
            </div>
        </form>
    </div>
</template>

<script>
export default {
    name: 'VacancyForm',
    props: {
        isAdmin: {
            type: Boolean,
            default: false,
        },
        eventId: {
            type: [String, Number],
            required: true,
        },
        activityId: {
            type: [String, Number],
            default: null,
            required: false,
        },
        isNew: {
            type: Boolean,
            default: true,
        },
        vacancy: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            selectedSkill: '',
            vacancyFormData: {
                id: null,
                activityId: null,
                requiredLevel: null,
                title: '',
                description: '',
                requiredSkills: [],
                allSkillsRequired: false,
                strictRequirements: false,
                status: null,
            },
            vacancyFormErrors: {
                activityId: null,
                requiredLevel: null,
                title: null,
                description: null,
                requiredSkills: null,
                allSkillsRequired: null,
                strictRequirements: null,
                status: null,
            },
            vacancyFormRules: {
                requiredLevel: [
                    {
                        required: true,
                        message: 'Please choose your required level',
                        trigger: 'change',
                    },
                ],
                title: [
                    {
                        required: true,
                        message: 'Please enter a title',
                        trigger: 'blur',
                    },
                ],
                description: [
                    {
                        required: true,
                        message: 'Please enter a description',
                        trigger: 'blur',
                    },
                ],
            },
        }
    },

    computed: {
        descriptions() {
            return this.$store.state.descriptions || {}
        },
        // status list excluding 'Deleted'
        filteredStatuses() {
            const list = this.descriptions.vacancyStatus || []
            return list.filter(s => {
                const v = (s.value || s.Value || '').toString().toLowerCase()
                const d = (s.description || s.Description || '').toString().toLowerCase()
                return v !== 'deleted' && d !== 'deleted'
            })
        },
        skills() {
            return this.$store.state.skills || [];
        },
    },

    created() {
        this.assignFromVacancy(this.vacancy)
    },

    watch: {
        activityId(newVal) {
            if (newVal) this.vacancyFormData.activityId = newVal
        },
        vacancy(newVal) {
            this.assignFromVacancy(newVal)
        }
    },

    methods: {
        // centralize mapping from incoming vacancy object to local form data
        assignFromVacancy(v) {
            const src = v || {}
            // support both camelCase and PascalCase keys from API
            const get = (keys, fallback) => {
                for (const k of keys) {
                    if (src[k] !== undefined && src[k] !== null) return src[k]
                }
                return fallback
            }

            this.vacancyFormData.id = get(['id','Id','ID'], null)
            this.vacancyFormData.activityId = this.activityId || get(['ActivityId','activityId','activity_id'], null)
            this.vacancyFormData.requiredLevel = get(['RequiredLevel','requiredLevel','required_level'], null)
            this.vacancyFormData.title = get(['Title','title'], '')
            this.vacancyFormData.description = get(['Description','description'], '')
            this.vacancyFormData.requiredSkills = Array.isArray(get(['RequiredSkills','requiredSkills','required_skills'], []))
                ? [...get(['RequiredSkills','requiredSkills','required_skills'], [])]
                : []
            this.vacancyFormData.allSkillsRequired = !!get(['AllSkillsRequired','allSkillsRequired','all_skills_required'], false)
            this.vacancyFormData.strictRequirements = !!get(['StrictSkillsRequirements','strictRequirements','strict_requirements'], false)
            this.vacancyFormData.status = get(['Status','status'], 'Open')
        },

        addSkill() {
            if (!this.isAdmin) return
            if (this.selectedSkill && !this.vacancyFormData.requiredSkills.includes(this.selectedSkill)) {
                this.vacancyFormData.requiredSkills.push(this.selectedSkill)
                this.selectedSkill = ''
            }
        },
        removeSkill(idx) {
            if (!this.isAdmin) return
            this.vacancyFormData.requiredSkills.splice(idx, 1)
        },
        handleSubmit() {
            if (!this.isAdmin) return
            if (this.activityId) this.vacancyFormData.activityId = this.activityId
            // emit mapped form with id (if exists) and eventId
            const payload = {
                id: this.vacancyFormData.id,
                ActivityId: this.vacancyFormData.activityId,
                EventId: this.eventId,
                Title: this.vacancyFormData.title,
                Description: this.vacancyFormData.description,
                RequiredLevel: this.vacancyFormData.requiredLevel,
                RequiredSkills: [...this.vacancyFormData.requiredSkills],
                AllSkillsRequired: !!this.vacancyFormData.allSkillsRequired,
                StrictSkillsRequirements: !!this.vacancyFormData.strictRequirements,
                Status: this.vacancyFormData.status,
            }
            this.$emit('saved', payload)
        },
        handleClose() {
            this.$emit('close')
        },
    },
}
</script>

<style scoped>
.vacancy-card {
    padding: 16px;
}

.card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.card-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.form-row {
    display: flex;
    flex-direction: column;
}

.form-row.full {
    grid-column: 1 / -1;
}

.field-label {
    font-size: 13px;
    color: #333;
    margin-bottom: 6px;
}

.field-input {
    padding: 8px 10px;
    border: 1px solid #e6e6e6;
    border-radius: 6px;
    width: 100%;
    box-sizing: border-box;
}

.field-input:focus {
    outline: none;
    border-color: #b3d4ff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.08);
}

.skills-controls {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 8px;
}

/* Ensure select controls take same space and align */
.skills-controls .el-select,
.skills-controls .wide,
.skills-controls .field-input {
    flex: 1;
    min-width: 0;
    /* allow flex to shrink properly */
}

/* Make Element select inner look like other inputs */
.skills-controls .el-select .el-input__inner,
.skills-controls .el-input__inner {
    padding: 8px 10px;
    height: 36px;
    box-sizing: border-box;
    border-radius: 6px;
}

/* Keep Add button aligned to the selects */
.skills-controls .btn {
    height: 36px;
    align-self: center;
    padding: 6px 10px;
}

.skills-list {
    margin: 0;
    padding-left: 16px;
    list-style: disc;
}

.skill-item {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 6px;
}

.options {
    display: flex;
    gap: 16px;
    align-items: center;
}

.option {
    display: flex;
    gap: 6px;
    align-items: center;
    font-size: 14px;
}

.form-actions {
    grid-column: 1 / -1;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
}

.btn {
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid transparent;
    cursor: pointer;
}

.btn-ghost {
    background: transparent;
    border-color: #dcdcdc;
    color: #333;
}

.btn-primary {
    background: #007bff;
    color: #fff;
    border-color: #007bff;
}

.btn-sm {
    padding: 4px 8px;
    font-size: 13px;
}
</style>